"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_SEED_RELATIVE_DAYS_LIMIT = void 0;
exports.appSeedRelativeDateIssue = appSeedRelativeDateIssue;
exports.resolveAppSeedCellValue = resolveAppSeedCellValue;
exports.resolveAppSeedRows = resolveAppSeedRows;
const zod_1 = require("zod");
const calendar_day_1 = require("./calendar-day");
/** Maximum absolute offset in days, or in weeks for a weekday anchor. */
exports.APP_SEED_RELATIVE_DAYS_LIMIT = 3650;
const WEEKDAYS = ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'];
const relativeOffset = zod_1.z
    .number()
    .int()
    .min(-exports.APP_SEED_RELATIVE_DAYS_LIMIT)
    .max(exports.APP_SEED_RELATIVE_DAYS_LIMIT);
const timeZone = zod_1.z
    .string()
    .trim()
    .min(1)
    .max(64)
    .refine(zone => (0, calendar_day_1.calendarDayFormat)(zone) !== null)
    .optional();
const AppSeedRelativeDateSchema = zod_1.z.union([
    zod_1.z.object({ relativeDays: relativeOffset, timeZone }).strict(),
    zod_1.z
        .object({
        weekday: zod_1.z.enum(WEEKDAYS),
        relativeWeeks: relativeOffset,
        timeZone,
    })
        .strict(),
]);
function namesRelativeDate(value) {
    return (!!value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        ['relativeDays', 'weekday', 'relativeWeeks'].some(key => Object.prototype.hasOwnProperty.call(value, key)));
}
/**
 * Why a cell value cannot be a relative date of a column of `columnType`, or
 * null when it is a valid relative date or no relative date at all.
 */
function appSeedRelativeDateIssue(columnType, value) {
    if (!namesRelativeDate(value))
        return null;
    const weekly = !Object.prototype.hasOwnProperty.call(value, 'relativeDays');
    if (columnType !== 'date')
        return ((weekly ? 'weekday/relativeWeeks' : 'relativeDays') +
            ' needs a date column');
    return AppSeedRelativeDateSchema.safeParse(value).success
        ? null
        : weekly
            ? `weekday must be mo, tu, we, th, fr, sa or su, relativeWeeks a whole number from -${exports.APP_SEED_RELATIVE_DAYS_LIMIT} to ${exports.APP_SEED_RELATIVE_DAYS_LIMIT}, timeZone a known time zone`
            : `relativeDays must be a whole number from -${exports.APP_SEED_RELATIVE_DAYS_LIMIT} to ${exports.APP_SEED_RELATIVE_DAYS_LIMIT}, timeZone a known time zone`;
}
/**
 * The cell value a date column stores for `value`: a relative date becomes
 * the UTC midnight of the day it names, counted from today in its time zone
 * (`now`, epoch milliseconds, is the instant that counts as today). Every
 * other value stays as it is.
 */
function resolveAppSeedCellValue(columnType, value, now = Date.now()) {
    if (columnType !== 'date' || !namesRelativeDate(value))
        return value;
    const date = AppSeedRelativeDateSchema.parse(value);
    const zone = date.timeZone ?? 'UTC';
    if ('relativeDays' in date)
        return Date.parse((0, calendar_day_1.calendarDayFromToday)(zone, date.relativeDays, now));
    // The weekday is taken from the local calendar day, never the UTC instant.
    const today = new Date((0, calendar_day_1.calendarDayFromToday)(zone, 0, now));
    const sinceMonday = (today.getUTCDay() + 6) % 7;
    const days = WEEKDAYS.indexOf(date.weekday) - sinceMonday + date.relativeWeeks * 7;
    return Date.parse((0, calendar_day_1.calendarDayFromToday)(zone, days, now));
}
/**
 * `rows` with each relative date cell resolved at `now`; other cells stay as
 * they are.
 */
function resolveAppSeedRows(columns, rows, now = Date.now()) {
    const types = new Map(columns.map(column => [column.id, column.type]));
    return rows.map(row => ({
        ...row,
        cells: Object.fromEntries(Object.entries(row.cells).map(([columnId, cell]) => [
            columnId,
            {
                ...cell,
                value: resolveAppSeedCellValue(types.get(columnId), cell.value, now),
            },
        ])),
    }));
}
